import TemplateIcon from "../../../assets/images/iconoir_stats-report.svg";
import menu from "../../../assets/images/icons/menu.svg";
import edit from "../../../assets/images/icons/edit_blue.svg";
import delete_icon from "../../../assets/images/icons/delete_black.svg";
import { useState } from "react";
import { Dropdown, GeneralButton, Modal } from "../../../components";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../Services/ToastContext";
import { EndPoints } from "../../../Constants/endpoints";
import { ApiError } from "../../../api/generalApi/ApiError";
import { usePostData } from "../../../hooks";
import { useTranslation } from "react-i18next";

interface TemplateCardProps {
  item: any;
  isEditable?: boolean;
  isSelectStyle?: boolean;
  route?: string;
  isSelected?: boolean;
  onSelect?: (templateId: string) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  item,
  isEditable,
  route,
  isSelectStyle,
  isSelected = false,
  onSelect,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { mutate, isPending } = usePostData(
    `${EndPoints.DeleteMedicalHistoryTypes}/${item?.Id}`,
    "DELETE",
  );

  const handleDelete = () => {
    try {
      mutate(
        {},
        {
          onSuccess: () => {
            showToast(t("deleted_successfully"), "success");
            setIsDeleteModalOpen(false);
            queryClient.invalidateQueries({
              queryKey: [EndPoints.MedicalHistoryTypes],
            });
          },
        },
      );
    } catch (error) {
      if (error instanceof ApiError) {
        showToast(error.message || t("failed_to_delete"), "error");
      } else {
        showToast("failed_to_delete", "error");
      }
    }
  };

  const operations: any[] = [
    {
      icon: edit,
      label: t("edit_template"),
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/medical-record-templates/${item?.Id}/customize`);
      },
      className: `!w-48 !font-normal hover:!text-primary hover:!font-bold`,
    },
    {
      icon: delete_icon,
      label: t("delete_template"),
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDeleteModalOpen(true);
      },
      className: `!w-48 !font-normal hover:!text-primary hover:!font-bold`,
    },
  ];

  const handleCardClick = () => {
    if (isSelectStyle && onSelect) {
      onSelect(item?.Id);
    } else if (route) {
      navigate(route);
    }
  };

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const borderClass = isSelected
    ? "border-2 border-primary"
    : "border border-[#065EEA38]";

  return (
    <>
      <div
        className={`cursor-pointer overflow-hidden rounded-lg border-2 transition-all hover:border-primary ${borderClass}`}
        onClick={handleCardClick}
      >
        <div className="align-center bg-primary-bg flex h-36 justify-center">
          <img className="w-6" alt="template" src={TemplateIcon} />
        </div>
        <div className="flex items-center justify-between p-3">
          <p className="text-sm">{item?.Name}</p>
          {isEditable && (
            <Dropdown
              menuItems={operations}
              children={
                <img
                  src={menu}
                  alt="menu"
                  className="h-5 w-5 cursor-pointer"
                  onClick={handleDropdownClick}
                />
              }
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />
          )}
          {!isEditable && !isSelectStyle && (
            <p className="text-xs font-normal">{t("by_dome_care")}</p>
          )}
        </div>
      </div>
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        header={t("delete_template")}
      >
        <p className="text-body-text mb-10 text-lg">
          {t("are_you_sure_you_want_to_delete_this_template")}
        </p>
        <div className="flex justify-end gap-4">
          <GeneralButton
            title={t("cancel")}
            color="lite"
            fontColor="black"
            onClick={() => setIsDeleteModalOpen(false)}
            disabled={isPending}
          />
          <GeneralButton
            color="red"
            title={t("delete")}
            onClick={handleDelete}
            isLoading={isPending}
          />
        </div>
      </Modal>
    </>
  );
};

export default TemplateCard;
